import { useMemo } from 'react'
import { Layout, Menu } from 'antd'
import {
  DashboardOutlined,
  AimOutlined,
  SendOutlined,
  CompassOutlined,
  CarOutlined,
  CloudOutlined,
  AlertOutlined,
  SettingOutlined,
  RadarChartOutlined,
  ContainerOutlined,
  HeartOutlined,
  PartitionOutlined,
  GlobalOutlined,
  NotificationOutlined,
  TeamOutlined,
  BulbOutlined,
  ReadOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  RobotOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { BUSINESS_MODULES, ADMIN_MODULES } from '@/shared/data/modules'

const { Sider } = Layout

const ICON_MAP: Record<string, React.ReactNode> = {
  'tac-chien': <AimOutlined />,
  'ke-hoach-bay': <SendOutlined />,
  'dan-duong': <CompassOutlined />,
  'van-tai': <CarOutlined />,
  'khi-tuong': <CloudOutlined />,
  'bao-dong': <AlertOutlined />,
  'hau-can': <ContainerOutlined />,
  'y-te': <HeartOutlined />,
  'dao-tao': <ReadOutlined />,
  workflow: <PartitionOutlined />,
  gis: <GlobalOutlined />,
  notif: <NotificationOutlined />,
  'user-admin': <TeamOutlined />,
  'dashboard-cmd': <DashboardOutlined />,
  innovation: <BulbOutlined />,
  'bao-cao': <FileTextOutlined />,
  'danh-muc': <DatabaseOutlined />,
  ai: <RobotOutlined />,
  help: <QuestionCircleOutlined />,
  admin: <SettingOutlined />,
}

interface SidebarProps {
  collapsed: boolean
  onCollapse: (collapsed: boolean) => void
}

export function Sidebar({ collapsed, onCollapse }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const selectedKey = useMemo(() => {
    const path = location.pathname.replace(/^\//, '').split('/')[0]
    return path || 'dashboard'
  }, [location.pathname])

  const items = useMemo(
    () => [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: 'Trang chủ',
      },
      {
        key: 'sch',
        icon: <RadarChartOutlined />,
        label: 'Sở chỉ huy',
      },
      {
        type: 'divider' as const,
      },
      ...BUSINESS_MODULES.map((m) => ({
        key: m.key,
        icon: ICON_MAP[m.key],
        label: m.shortTitle,
      })),
      {
        type: 'divider' as const,
      },
      ...ADMIN_MODULES.map((m) => ({
        key: m.key,
        icon: ICON_MAP[m.key],
        label: m.shortTitle,
      })),
    ],
    [],
  )

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={232}
      collapsedWidth={64}
      theme="dark"
      style={{ height: '100vh', position: 'sticky', top: 0, left: 0 }}
      className="!border-r border-[var(--color-border-subtle)] [&>.ant-layout-sider-children]:!flex [&>.ant-layout-sider-children]:!flex-col"
    >
      <div
        className="shrink-0 flex items-center gap-2 px-4 h-14 border-b border-[var(--color-border-subtle)] cursor-pointer"
        onClick={() => navigate('/dashboard')}
      >
        <div className="w-8 h-8 rounded bg-[var(--color-primary-500)] flex items-center justify-center text-white font-bold text-sm shrink-0">
          PK
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">
              PK-KQ
            </span>
            <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">
              CĐS 2026–2030
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          items={items}
          onClick={({ key }) => {
            if (key === 'dashboard') navigate('/dashboard')
            else navigate(`/${key}`)
          }}
          className="!border-none !bg-transparent pt-2"
        />
      </div>
    </Sider>
  )
}
